// Ordner der E-Mail-Sidebar (Handoff E-Mails §6): Reihenfolge, Labels, Zähler – reine Funktionen.

import type { EmailNavState, EmailOrdner } from '../../../pages/admin/emails/EmailNavContext';

export type FesterOrdner = Exclude<EmailOrdner, 'objekt'>;

export const EMAIL_ORDNER: readonly { key: FesterOrdner; label: string }[] = [
  { key: 'posteingang', label: 'Posteingang' },
  { key: 'offen', label: 'Offen' },
  { key: 'erledigt', label: 'Erledigt' },
  { key: 'wiedervorlage', label: 'Wiedervorlage' },
  { key: 'rueckmeldung', label: 'Rückmeldung' },
  { key: 'gesendet', label: 'Gesendet' },
  { key: 'geplant', label: 'Geplant' },
  { key: 'entwuerfe', label: 'Entwürfe' },
  { key: 'werbung', label: 'Werbung' },
  { key: 'archiviert', label: 'Archiviert' },
  { key: 'geloescht', label: 'Gelöscht' },
  { key: 'vorlagen', label: 'Vorlagen' },
  { key: 'serienmail', label: 'Serienmail' },
  { key: 'einstellungen', label: 'Einstellungen' },
];

export const ordnerLabel = (key: EmailOrdner): string => EMAIL_ORDNER.find(o => o.key === key)?.label ?? 'Objekt';

type Zaehler = Pick<EmailNavState, 'unreadCount' | 'offenCount' | 'wiedervorlageCount' | 'rueckmeldungCount' | 'geplantCount' | 'entwuerfeCount' | 'werbungCount'>;

/** Zähler je Ordner wie in der bisherigen Sidebar (Archiviert, Erledigt, Gesendet … ohne). Serienmail = laufende Läufe. */
export function ordnerZaehler(key: FesterOrdner, z: Zaehler, laufendeSerienmails: number): number | null {
  const n = {
    posteingang: z.unreadCount, offen: z.offenCount, wiedervorlage: z.wiedervorlageCount, rueckmeldung: z.rueckmeldungCount, geplant: z.geplantCount,
    entwuerfe: z.entwuerfeCount, werbung: z.werbungCount, serienmail: laufendeSerienmails,
  }[key as string];
  return typeof n === 'number' && n > 0 ? n : null;
}

/** Ordnerliste ohne Serienmail, wenn das Modul nicht freigeschaltet ist. */
export const sichtbareOrdner = (serienmailErlaubt: boolean) => EMAIL_ORDNER.filter(o => o.key !== 'serienmail' || serienmailErlaubt);

/** „Letzter Sync vor 3 Min.“ */
export function syncText(letzterSync: string | null | undefined, jetzt: Date): string | null {
  if (!letzterSync) return null;
  const t = new Date(letzterSync).getTime();
  if (Number.isNaN(t)) return null;
  const min = Math.max(0, Math.floor((jetzt.getTime() - t) / 60000));
  if (min < 1) return 'Letzter Sync gerade eben';
  if (min < 60) return `Letzter Sync vor ${min} Min.`;
  const std = Math.floor(min / 60);
  if (std < 24) return `Letzter Sync vor ${std} Std.`;
  return `Letzter Sync am ${new Date(t).toLocaleDateString('de-DE')}`;
}

export const objektOrdnerLabel = (o: { objektnummer: string | null; bezeichnung: string | null }) =>
  [o.objektnummer, o.bezeichnung].filter(Boolean).join(' · ') || 'Objekt';

/** #578: Ordner, auf die man Mails ziehen kann (wie die Objekte). */
export const ZIEH_ORDNER: readonly FesterOrdner[] = ['posteingang', 'offen', 'erledigt', 'werbung', 'archiviert'];
export const istZiehOrdner = (key: FesterOrdner): boolean => ZIEH_ORDNER.includes(key);

export interface ZiehAenderung {
  /** true/false = Werbung setzen/aufheben (über setzeWerbungManuell), undefined = unverändert */
  werbung?: boolean;
  patch: { erledigt?: boolean; archiviert_am?: string | null; wiedervorlage_am?: null; in_bearbeitung_von?: null; in_bearbeitung_seit?: null };
}

/**
 * Was „Mail auf Ordner ziehen“ bedeutet. Offen/Posteingang holen eine Mail aus Werbung, Erledigt
 * und Archiv zurück; Erledigt/Archiviert heben Werbung ebenfalls auf (wer sie dort ablegt, hält sie
 * nicht für Werbung).
 */
export function ziehAenderung(ziel: FesterOrdner, email: { ist_werbung?: boolean | null }, jetztIso: string): ZiehAenderung | null {
  const keineWerbung = email.ist_werbung ? false : undefined;
  switch (ziel) {
    case 'posteingang':
    case 'offen':
      return { werbung: keineWerbung, patch: { erledigt: false, archiviert_am: null } };
    case 'erledigt':
      return { werbung: keineWerbung, patch: { erledigt: true, archiviert_am: null, wiedervorlage_am: null, in_bearbeitung_von: null, in_bearbeitung_seit: null } };
    case 'werbung':
      return email.ist_werbung ? null : { werbung: true, patch: {} };
    case 'archiviert':
      return { werbung: keineWerbung, patch: { archiviert_am: jetztIso, wiedervorlage_am: null } };
    default:
      return null;
  }
}
